'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Bug, X, Trash2, ChevronDown, ChevronUp, AlertTriangle, AlertCircle } from 'lucide-react';

interface DebugEntry {
  id: string;
  timestamp: string;
  phase: string;
  message: string;
  stack?: string;
}

declare global {
  interface Window {
    __debugLog?: (phase: string, message: string, type: 'error' | 'warning' | 'info', stack?: string) => void;
  }
}

export default function DebugWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<DebugEntry[]>([]);
  const [warnings, setWarnings] = useState<DebugEntry[]>([]);
  const [activeTab, setActiveTab] = useState<'errors' | 'warnings'>('errors');
  const [flash, setFlash] = useState<'red' | 'amber' | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const triggerFlash = useCallback((color: 'red' | 'amber') => {
    setFlash(color);
    setTimeout(() => setFlash(null), 1500);
  }, []);

  useEffect(() => {
    const now = () => new Date().toLocaleTimeString('el-GR');
    const uid = () => Math.random().toString(36).slice(2, 9);

    window.__debugLog = (phase: string, message: string, type: 'error' | 'warning' | 'info', stack?: string) => {
      if (type === 'info') {
        console.info(`[${phase}] ${message}`);
        return;
      }
      const entry: DebugEntry = {
        id: uid(),
        timestamp: now(),
        phase,
        message: message || 'Άγνωστο σφάλμα',
        stack: stack || undefined,
      };
      if (type === 'error') {
        setErrors(prev => [entry, ...prev]);
        triggerFlash('red');
      } else {
        setWarnings(prev => [entry, ...prev]);
        triggerFlash('amber');
      }
    };

    const origError = window.onerror;
    window.onerror = (msg, src, line, col, error) => {
      const message = error?.message || String(msg) || 'Άγνωστο σφάλμα';
      const stack = error?.stack
        ? error.stack
        : src
        ? `at ${src}:${line}:${col}`
        : undefined;
      window.__debugLog?.('Global', message, 'error', stack);
      if (origError) return origError(msg, src, line, col, error);
      return false;
    };

    const handleUnhandled = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const message = reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
        ? reason
        : JSON.stringify(reason) || 'Unhandled promise rejection';
      const stack = reason instanceof Error ? reason.stack : undefined;
      window.__debugLog?.('Promise', message, 'error', stack);
    };
    window.addEventListener('unhandledrejection', handleUnhandled);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandled);
    };
  }, [triggerFlash]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalCount = errors.length + warnings.length;
  const buttonColor = flash === 'red' ?'bg-red-600 text-white'
    : flash === 'amber' ?'bg-amber-500 text-white'
    : totalCount > 0
    ? 'bg-gray-800 text-white' :'bg-gray-200 text-gray-600';

  const displayList = activeTab === 'errors' ? errors : warnings;

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Toggle bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${buttonColor}`}
        >
          <Bug size={14} />
          <span>Debug</span>
          {totalCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
              {totalCount}
            </span>
          )}
          {isOpen ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
        <span className="text-xs text-gray-400">AskΦόρος — Debug Panel</span>
      </div>

      {/* Panel */}
      {isOpen && (
        <div className="min-h-[300px] max-h-[500px] overflow-y-auto scrollbar-thin animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
            <span className="text-sm font-semibold text-gray-700">Debug — AskΦόρος</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setErrors([]); setWarnings([]); }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                <Trash2 size={12} />
                Καθαρισμός
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-white sticky top-10 z-10">
            <button
              onClick={() => setActiveTab('errors')}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === 'errors' ?'border-red-500 text-red-600' :'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <AlertCircle size={13} />
              Σφάλματα
              {errors.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">
                  {errors.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('warnings')}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === 'warnings' ?'border-amber-500 text-amber-600' :'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <AlertTriangle size={13} />
              Προειδοποιήσεις
              {warnings.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs">
                  {warnings.length}
                </span>
              )}
            </button>
          </div>

          {/* Entries */}
          <div className="p-3 space-y-2">
            {displayList.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Δεν υπάρχουν {activeTab === 'errors' ? 'σφάλματα' : 'προειδοποιήσεις'}
              </div>
            ) : (
              displayList.map((entry) => (
                <div
                  key={`debug-${entry.id}`}
                  className={`rounded-md border p-3 text-xs ${
                    activeTab === 'errors' ?'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                        activeTab === 'errors' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                      }`}>
                        {entry.phase}
                      </span>
                      <span className="text-gray-400">{entry.timestamp}</span>
                    </div>
                    {entry.stack && (
                      <button
                        onClick={() => toggleExpand(entry.id)}
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                      >
                        {expandedIds.has(entry.id) ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                  <p className={`mt-1 font-medium break-words ${
                    activeTab === 'errors' ? 'text-red-800' : 'text-amber-800'
                  }`}>
                    {entry.message}
                  </p>
                  {entry.stack && expandedIds.has(entry.id) && (
                    <pre className="mt-2 text-xs text-gray-500 overflow-x-auto whitespace-pre-wrap font-mono bg-white/50 p-2 rounded">
                      {entry.stack}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}